// Server error codes a normal user can actually hit, mapped to text they can
// act on.
//
// The two that matter most in the hosted product are capacity_reached and
// rate_limit_exceeded: inference runs on a small amount of self-hosted GPU, so
// "come back in a few minutes" is a routine outcome rather than a bug. Both
// used to surface as a silently swallowed no-op — the user clicked Generate and
// nothing happened at all.
//
// Anything not listed here falls through to the raw server message. A wrong
// reassurance is worse than a technical string.

import { ApiClientError } from './api';

type Msg = { zh: string; en: string };

const CODES: Record<string, Msg> = {
  capacity_reached: {
    zh: '当前排队的任务已满，说明大家都在用。请过几分钟再试一次——你的输入不会丢。',
    en: 'The queue is full right now. Try again in a few minutes — your input is not lost.',
  },
  rate_limit_exceeded: {
    zh: '今天的生成次数已经用完了。免费额度每天重置，明天再来即可。',
    en: 'You have used up today\'s generations. The free allowance resets daily.',
  },
  title_filter_incomplete: {
    zh: '还没设置求职方向，无法开始扫描。请先在「我的资料」里填写目标岗位。',
    en: 'Set your target roles in Profile before scanning.',
  },
  invite_required: {
    zh: '注册需要邀请码。',
    en: 'An invite code is required to register.',
  },
  db_unavailable: {
    zh: '数据库暂时连不上，请稍后再试。',
    en: 'The database is temporarily unavailable. Please try again shortly.',
  },
};

/**
 * Turn a thrown value into something worth showing a user.
 * `zh` picks the language; unknown codes fall through to the raw message.
 */
export function friendlyError(err: unknown, zh: boolean): string {
  const fallback = zh ? '出错了，请稍后再试。' : 'Something went wrong. Please try again.';
  if (!(err instanceof Error)) return fallback;

  // ApiClientError sets .message from the server's `error` field, so the code
  // and the message are the same string for these.
  const key = (err instanceof ApiClientError ? err.code || err.message : err.message) || '';
  const hit = CODES[key];
  if (hit) return zh ? hit.zh : hit.en;

  // Offline / DNS / connection refused all land here as a TypeError.
  if (err instanceof TypeError) {
    return zh ? '网络连接失败，请检查网络后重试。' : 'Network request failed. Check your connection and retry.';
  }
  return err.message || fallback;
}
